import React from 'react';

// Custom cell renderer for status badge
export const StatusBadgeRenderer = (props) => {
  const status = props.data?.contact_status_name || props.value || 'New';
  const badgeClass = {
    'New': 'bg-secondary',
    'Contacted': 'bg-info',
    'Qualified': 'bg-primary',
    'Proposal Sent': 'bg-warning',
    'Negotiation': 'bg-warning',
    'Won': 'bg-success',
    'Lost': 'bg-danger'
  }[status] || 'bg-secondary';

  return <span className={`badge ${badgeClass}`}>{status}</span>;
};

// Custom cell renderer for priority
export const PriorityBadgeRenderer = (props) => {
  const priority = props.data?.priority_name || props.value || 'Medium';
  const badgeClass = {
    'Low': 'bg-secondary',
    'Medium': 'bg-info',
    'High': 'bg-danger',
    'Urgent': 'bg-danger'
  }[priority] || 'bg-info';

  return <span className={`badge ${badgeClass}`}>{priority}</span>;
};

// Custom cell renderer for URLs
export const UrlCellRenderer = (props) => {
  if (!props.value) return null;
  return (
    <a href={props.value} target="_blank" rel="noopener noreferrer" className="text-primary">
      <i className="fas fa-external-link-alt me-1"></i>
      Link
    </a>
  );
};

// Custom cell renderer for company logo
export const LogoCellRenderer = (props) => {
  const logo = props.data.company_object?.logo;
  if (!logo) return null;
  return (
    <img
      src={logo}
      alt="Company Logo"
      style={{ width: '32px', height: '32px', objectFit: 'contain' }}
    />
  );
};

// Custom cell renderer for boolean values
export const BooleanCellRenderer = (props) => {
  if (props.value === true) {
    return <i className="fas fa-check-circle text-success"></i>;
  } else if (props.value === false) {
    return <i className="fas fa-times-circle text-muted"></i>;
  }
  return null;
};

// Custom cell renderer for qualified status with date
export const QualifiedCellRenderer = (props) => {
  const isQualified = props.value === true;
  const qualifiedDate = props.data?.qualified_date;

  if (isQualified) {
    const formattedDate = qualifiedDate ? new Date(qualifiedDate).toLocaleString('en-US', {
      month: 'numeric', day: 'numeric', year: '2-digit', hour: 'numeric', minute: '2-digit', hour12: true
    }) : '';

    return (
      <div className="d-flex align-items-center gap-1">
        <i className="fas fa-check-circle text-success me-1"></i>
        {formattedDate && <span className="text-muted small">{formattedDate}</span>}
      </div>
    );
  } else if (props.value === false) {
    return <i className="fas fa-times-circle text-muted"></i>;
  }
  return null;
};

// Custom cell renderer for long text (truncate with tooltip)
export const LongTextCellRenderer = (props) => {
  if (!props.value) return '';
  const maxLength = 150;
  const text = String(props.value);
  const truncated = text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  return truncated;
};

// Custom cell renderer for job title with job posting link
export const JobTitleRenderer = (props) => {
  const jobTitle = props.value || '';
  const jobUrl = props.data?.url;

  if (!jobTitle) return '';

  if (jobUrl) {
    return (
      <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
        <span className="text-truncate" style={{ minWidth: 0 }}>{jobTitle}</span>
        <a href={jobUrl} target="_blank" rel="noopener noreferrer" className="text-primary flex-shrink-0" title="View job posting">
          <i className="fas fa-external-link-alt"></i>
        </a>
      </div>
    );
  }

  return <span>{jobTitle}</span>;
};

// Custom cell renderer for company name with linkout and Google search icons
export const CompanyNameRenderer = (props) => {
  const companyName = props.value || '';
  const companyUrl = props.data?.company_object?.url;
  const googleSearchUrl = `https://www.google.com/search?q=healthcare+organization+profile+${encodeURIComponent(companyName)}`;

  return (
    <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
      <span className="text-truncate" style={{ minWidth: 0 }}>{companyName}</span>
      {/* Company URL linkout icon */}
      {companyUrl ? (
        <a href={companyUrl} target="_blank" rel="noopener noreferrer" className="text-primary flex-shrink-0" title="Visit company website" >
          <i className="fas fa-external-link-alt"></i>
        </a>
      ) : (
        <i className="fas fa-external-link-alt text-secondary flex-shrink-0" title="Company URL not available" style={{ opacity: 0.5, cursor: 'not-allowed' }}></i>
      )}
      {/* Google search icon */}
      <a href={googleSearchUrl} target="_blank" rel="noopener noreferrer" className="text-primary flex-shrink-0" title="Search on Google" >
        <i className="fab fa-google"></i>
      </a>
    </div>
  );
};

// Custom cell renderer for job source with domain linkout and job ID
export const JobSourceRenderer = (props) => {
  const source = props.value || '';
  const sourceDomain = props.data?.source_domain;
  if (!source) return '';
  // Ensure URL has proper protocol
  const formatUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    return `https://${url}`;
  };
  const formattedUrl = formatUrl(sourceDomain);
  return (
    <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
      <span className="text-truncate" style={{ minWidth: 0 }}>{source}</span>
      {formattedUrl && (
        <a href={formattedUrl} target="_blank" rel="noopener noreferrer" className="text-primary flex-shrink-0" title="Visit source">
          <i className="fas fa-external-link-alt"></i>
        </a>
      )}
    </div>
  );
};

// Combined cell renderer for Status, Priority, and Score
export const StatusPriorityScoreRenderer = (props) => {
  const status = props.data?.contact_status_name || 'New';
  const priority = props.data?.priority_name || 'Medium';
  const score = props.data?.lead_score;

  const statusBadgeClass = {
    'New': 'bg-secondary',
    'Contacted': 'bg-info',
    'Qualified': 'bg-primary',
    'Proposal Sent': 'bg-warning',
    'Negotiation': 'bg-warning',
    'Won': 'bg-success',
    'Lost': 'bg-danger'
  }[status] || 'bg-secondary';

  const priorityBadgeClass = {
    'Low': 'bg-secondary',
    'Medium': 'bg-info',
    'High': 'bg-danger',
    'Urgent': 'bg-danger'
  }[priority] || 'bg-info';

  return (
      <div className="d-flex align-items-center gap-1">
        <span className={`badge ${statusBadgeClass} small`}>{status}</span>
        <span className={`badge ${priorityBadgeClass} small`}>{priority}</span>
        <span className="">{score}/10</span>     
      </div>
  );
};

// Custom cell renderer for employment type (prefers regular, falls back to AI)
export const EmploymentTypeRenderer = (props) => {
  const employmentType = props.data?.employment_type || props.data?.ai_employment_type || '';
  return employmentType;
};

// Custom cell renderer for location with Google search icon
export const LocationRenderer = (props) => {
  const location = props.value || '';
  if (!location) return '';
  const googleSearchUrl = `https://www.google.com/search?q=quick+facts+${encodeURIComponent(location)}`;
  return (
    <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
      <span className="text-truncate" style={{ minWidth: 0 }}>{location}</span>
      {/* Google search icon */}
      <a href={googleSearchUrl} target="_blank" rel="noopener noreferrer" className="text-primary flex-shrink-0" title="Search quick facts on Google">
        <i className="fab fa-google"></i>
      </a>
    </div>
  );
};

// Value formatter for dates
export const dateFormatter = (params) => {
  if (!params.value) return '';
  try {
    return new Date(params.value).toLocaleDateString('en-US');
  } catch {
    return params.value;
  }
};
